import {
  Component,
  computed,
  effect,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import {BuildingsService} from "../../../common/services/buildings.service";
import {BuildingSummary} from "../../../common/model/building-summary";
import {map, tap} from "rxjs/operators";
import {FormControl} from "@angular/forms";
import {BuildingDetailsService} from "../../details/building-details.service";
import {IdAndLabel} from "../../../common/model/id-and-label";
import {Localization} from "../../../common/enums/localization";
import {StructureType} from "../../../common/enums/enums";
import {toSignal} from '@angular/core/rxjs-interop';
import {Activity} from '../../../../helpers/activity';

function siteIdAlteredIn(changes: SimpleChanges) {
  return changes.siteId && (changes.siteId.currentValue !== changes.siteId.previousValue);
}

@Component({
  selector: 'app-building-selection',
  templateUrl: './building-selection.component.html',
  styleUrls: ['./building-selection.component.scss'],
})
export class BuildingSelectionComponent implements OnChanges, OnInit {
  public readonly Localization = Localization;

  @Input()
  public siteId: string;
  @Input()
  public clearable = true;

  @Input()
  public buildingId: string;
  @Input()
  public building: BuildingSummary;
  @Output()
  public buildingChange = new EventEmitter<BuildingSummary>();
  protected readonly selectionField = new FormControl<number>(null);

  protected selection = toSignal(this.selectionField.valueChanges);
  protected selectedBuilding = computed(() => this.buildings().find(({id}) => id === this.selection()));
  protected selectedBuildingCoverImageUrl = computed(() => this.buildingDetails.getBuildingMultimediumUrl(this.selectedBuilding()));
  protected selectedBuildingReference = computed(() => this.selectedBuilding()?.reference);

  protected buildings = signal<BuildingSummary[]>([]);
  protected buildingOptions = computed(() => this.buildings().map(buildingAsOption));

  protected readonly activity: Activity<BuildingSelectionOperations> = new Activity(BuildingSelectionOperations);

  constructor(
    private readonly buildingsService: BuildingsService,
    private readonly buildingDetails: BuildingDetailsService,

  ) {
    effect(() => this.buildingChange.emit(this.selectedBuilding()));
  }

  public async ngOnInit() {
    await this.fetchBuildings();
      this.selectionField.setValue(this.building?.id || parseInt(this.buildingId));
  }

  public async ngOnChanges(changes:SimpleChanges) {
    if (siteIdAlteredIn(changes)) {
      await this.fetchBuildings();

    }
  }


  public fetchBuildings() {
    const siteId = this.siteId ? parseInt(this.siteId) : null;
    const loadBuildingsRequest = this.buildingsService.getAllBuildingsIncludingChildren(siteId).pipe(
      map(sitesAndBuildings => sitesAndBuildings.filter(({type}) => type ===StructureType.BUILDING)),
      tap(buildings => this.buildings.set(buildings))
    );
    return this.activity
      .track(BuildingSelectionOperations.LoadBuildings)
      .operation(loadBuildingsRequest.toPromise());
  }
}


function buildingPath(building: BuildingSummary): string {
  const {label, parentStructureLabel} = building;
  return [parentStructureLabel, label].filter(Boolean).join(' / ');
}

function buildingAsOption(building: BuildingSummary): IdAndLabel {
  return new IdAndLabel(building.id, buildingPath(building));
}

enum BuildingSelectionOperations {
  LoadBuildings,
}
